import React, { useState, useEffect } from 'react';
import Layout from 'Components/Layout';
import UserClasses from '../users/Users.module.css';
import ProductClasses from '../products/Products.module.css';
import OrderClasses from '../orders/Orders.module.css';
import { Helmet } from 'react-helmet';
import { Button, Box, IconButton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryClasses from './AddDeliveryBoy.module.css';
import { BsCloudUpload, BsEye, BsEyeSlash } from 'react-icons/bs';
import ReactLoading from 'react-loading';
import { Services } from 'Services';
import { Success, Error } from 'Util/Toast';

const EditDeliveryBoy = () => {
    const navigate = useNavigate();
    const { deliveryPersonId } = useParams();

    const [imagePreview, setImagePreview] = useState('');
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Initialize formData state with default values
    const initialFormData = {
        firstName: '',
        lastName: '',
        email: '',
        mobileNumber: '',
        password: '',
        imageId: '',
        role: '',
        salaryPerMonth: ''
    };

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => {
        const token = { Authorization: `token ${localStorage.getItem('Token')}` };

        // Fetch delivery person details by ID
        Services.getDeliveryPersonById(deliveryPersonId, token)
            .then((response) => {
                if (response.Status === 1) {
                    const deliveryPerson = response.DeliveryPerson;
                    setFormData({
                        firstName: deliveryPerson.deliveryPersonFirstName,
                        lastName: deliveryPerson.deliveryPersonLastName,
                        email: deliveryPerson.deliveryPersonEmail,
                        mobileNumber: deliveryPerson.deliveryPersonPhone,
                        password: deliveryPerson.password,
                        role: deliveryPerson.deliveryPersonRole.toString(),
                        salaryPerMonth: deliveryPerson.deliveryPersonSalary.toString(),
                        imageId: deliveryPerson.deliveryPersonImageId,
                    });

                    // Set existing image preview and uploaded file name
                    setImagePreview(deliveryPerson.deliveryPersonImagePreview);
                    setUploadedFileName(deliveryPerson.deliveryPersonImageId);
                } else {
                    console.error('Error fetching delivery person:', response);
                    Error('Failed to fetch delivery person details');
                }
            })
            .catch((error) => {
                console.error('Error fetching delivery person:', error);
                Error('Failed to fetch delivery person details');
            });
    }, [deliveryPersonId]);

    // Handle form input changes with validations
    const handleChange = (event) => {
        const { name, value } = event.target;
        let updatedValue = value;

        // Validation using regex patterns
        switch (name) {
            case 'email':
                // Basic email format validation
                updatedValue = value.trim();
                break;
            case 'mobileNumber':
                // Mobile number format validation (allowing only 10 digits)
                updatedValue = value.replace(/\D/g, '').slice(0, 10);
                break;
            case 'password':
                // Password length validation (at least 6 characters)
                updatedValue = value;
                break;
            case 'salaryPerMonth':
                // Salary should not be negative
                updatedValue = value >= 0 ? value : formData.salaryPerMonth;
                break;
            default:
                updatedValue = value;
                break;
        }

        // Update formData with validated value
        setFormData({ ...formData, [name]: updatedValue });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            Error(`${file.name} is not a valid image format.`);
            return;
        }

        let formDataForUpload = new FormData();
        formDataForUpload.append("image", file);
        formDataForUpload.append("folder", "delivery-persons");

        try {
            setImageLoading(true);
            const res = await Services.UploadImage(formDataForUpload);
            if (res?.Status === 1) {
                const uploadedUrl = res?.ImageURL;
                setUploadedFileName(res?.ImageID);
                setImagePreview(uploadedUrl || URL.createObjectURL(file));
                setFormData(prev => ({ ...prev, imageId: res?.ImageID }));
                setImageLoading(false);
            } else {
                Error(res?.Message || "Upload failed.");
                setImageLoading(false);
            }
        } catch (err) {
            console.error("Upload Error:", err);
            Error("Upload error. Please try again.");
            setImageLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();
        const token = { Authorization: `token ${localStorage.getItem('Token')}` };

        setLoading(true);

        // Update formData with the uploaded image ID before sending
        const updatedFormData = {
            ...formData,
            imageId: uploadedFileName
        };

        // Update delivery person API call
        Services.editDeliveryPerson(deliveryPersonId, updatedFormData, token)
            .then((response) => {
                if (response.Status) {
                    navigate('/deliveryboymanagement');
                    Success(response.Message);
                    console.log('Delivery person updated successfully:', response);
                } else {
                    Error(response.Message);
                }
            })
            .catch((error) => {
                console.error('Error updating delivery person:', error);
                Error('Failed to update delivery person');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // UI for file upload
    const fileuploadDesign = (
        <div className={DeliveryClasses["add-item"]} onClick={() => document.getElementById("image").click()}>
            <div className={DeliveryClasses['upload-image']}>
                {imagePreview && <img src={imagePreview} alt="Selected" className={DeliveryClasses['uploaded-image']} style={{height: 150, width: 150, objectFit: 'cover'}} />}
                {imageLoading && <ReactLoading className={ProductClasses['loading-spinner-container']} type="spin" color="#000" height={50} width={50} />}
                {!imagePreview && (
                    <div className={DeliveryClasses['add-item']}>
                        <BsCloudUpload className={DeliveryClasses["upload-icon"]} />
                        <h5>Drag Your Files here</h5>
                        <p>(Only *.jpeg, *.png, *.webp will be accepted)</p>
                    </div>
                )}
                <input type="file" id="image" onChange={handleImageUpload} style={{ display: 'none' }} />
            </div>
        </div>
    );

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Layout Active={'DeliveryBoyManagement'}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Edit Delivery Boy</title>
            </Helmet>
            <div className={UserClasses['main-first-container']}>
                <div className={UserClasses['main-data-tab']}>
                    <div className={UserClasses['add-users-btn']}>
                        <Button variant="contained" color="primary" onClick={() => navigate(-1)}>
                            BACK
                        </Button>
                    </div>
                    <div className={UserClasses['users-main']}>
                        <h2 className={OrderClasses['header-form']}>
                            Edit Delivery Boy
                        </h2>
                        <form className={DeliveryClasses['form-container']} onSubmit={handleSubmit} data-aos="fade-up"
                            data-aos-anchor-placement="top-bottom">
                            <Box mb={2}>
                                <input
                                    className={DeliveryClasses['form-textfields']}
                                    name="firstName"
                                    placeholder="First Name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </Box>
                            <Box mb={2}>
                                <input
                                    className={DeliveryClasses['form-textfields']}
                                    name="lastName"
                                    placeholder="Last Name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                />
                            </Box>
                            <Box mb={2}>
                                <input
                                    className={DeliveryClasses['form-textfields']}
                                    name="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    type="email"
                                />
                            </Box>
                            <Box mb={2}>
                                <input
                                    className={DeliveryClasses['form-textfields']}
                                    name="mobileNumber"
                                    placeholder="Mobile Number"
                                    value={formData.mobileNumber}
                                    onChange={handleChange}
                                    type="tel"
                                />
                            </Box>
                            <Box mb={2}>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className={DeliveryClasses['form-textfields']}
                                        name="password"
                                        placeholder="Password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        minLength={6}
                                    />
                                    <IconButton
                                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                                        onClick={togglePasswordVisibility}
                                    >
                                        {!showPassword ? <BsEyeSlash /> : <BsEye />}
                                    </IconButton>
                                </div>
                            </Box>
                            <Box mb={2}>
                                <select
                                    className={DeliveryClasses['form-textfields']}
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Role</option>
                                    <option value="1">Delivery Boy</option>
                                </select>
                            </Box>
                            <Box mb={2}>
                                <input
                                    className={DeliveryClasses['form-textfields']}
                                    name="salaryPerMonth"
                                    placeholder="Salary Per Month"
                                    value={formData.salaryPerMonth}
                                    onChange={handleChange}
                                    type="number"
                                />
                            </Box>
                            <div className={DeliveryClasses['file-upload-wrapper']}>
                                <header>
                                    <h3>
                                        Upload Image
                                    </h3>
                                </header>
                                <div className={DeliveryClasses["upload-image"]}>
                                    {fileuploadDesign}
                                </div>
                            </div>
                            <Box mt={2}>
                                <div className={ProductClasses['form-btn-main']}>
                                    <Button 
                                        type="submit" 
                                        disabled={loading || imageLoading}
                                        variant="contained"
                                    >
                                        {loading ? 'Updating Delivery Boy...' : 'Update Delivery Boy'}
                                    </Button>
                                </div>
                            </Box>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default EditDeliveryBoy;